import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { ArrowLeft, ScrollText, ChevronDown, ChevronUp, Users } from "lucide-react";

function statusLabel(score) {
  if (score === null || score === undefined) return "—";
  if (score >= 90) return "Excellent";
  if (score >= 80) return "Good";
  if (score >= 70) return "Needs Attention";
  return "Needs Improvement";
}
function badgeCls(score) {
  if (score === null || score === undefined) return "text-[color:var(--text-secondary)] border-[color:var(--border)]";
  if (score >= 90) return "text-emerald-400 border-emerald-400/40 bg-emerald-500/10";
  if (score >= 80) return "text-[color:var(--gold)] border-[color:var(--gold)]/40 bg-[color:var(--gold)]/10";
  if (score >= 70) return "text-[color:var(--warning)] border-[color:var(--warning)]/40 bg-[color:var(--warning)]/10";
  return "text-[color:var(--error)] border-[color:var(--error)]/40 bg-[color:var(--error)]/10";
}

export default function MyDisplayReports() {
  const [rows, setRows] = useState(null);
  const [expanded, setExpanded] = useState({});
  useEffect(() => {
    api.get("/my_display_reports").then(({ data }) => setRows(data.rows || []))
       .catch((e) => { toast.error(formatApiError(e)); setRows([]); });
  }, []);
  if (rows === null) return <div className="text-[color:var(--text-secondary)]">Loading…</div>;
  return (
    <div className="space-y-6 max-w-4xl mx-auto" data-testid="my-reports-root">
      <Link to="/dashboard" className="text-[color:var(--text-secondary)] flex items-center gap-1 hover:text-[color:var(--gold)]"><ArrowLeft size={14}/> Dashboard</Link>
      <div>
        <div className="label-caps">Reports</div>
        <h1 className="font-heading text-3xl sm:text-4xl flex items-center gap-3"><ScrollText size={26}/> My Display Report</h1>
        <div className="text-sm text-[color:var(--text-secondary)] mt-1">
          Full history of every Zone inspection where you were selected as a Zone Owner. Historical records remain here even if the GM removes a Display Check from the active list.
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="hos-card p-8 text-center text-[color:var(--text-secondary)]">No historical records yet.</div>
      ) : (
        <div className="hos-card p-0 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="text-left label-caps">
              <tr>
                <th className="p-3">Date</th><th>Check</th><th>Zone</th><th>Fandoms</th><th className="text-right">Score</th><th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const key = `${r.check_id}::${r.zone_id}`;
                const ex = expanded[key];
                return (
                  <React.Fragment key={key}>
                    <tr className="border-t border-[color:var(--border)]">
                      <td className="p-3 whitespace-nowrap">
                        {r.inspection_date}
                        {r.is_archived && <div className="text-[10px] text-[color:var(--warning)] uppercase">Archived</div>}
                      </td>
                      <td>{r.check_number}</td>
                      <td>
                        <span className="font-semibold">{r.zone_name}</span>
                        {r.co_owner_count > 0 && (
                          <div className="text-[10px] text-[color:var(--text-secondary)] flex items-center gap-1"><Users size={10}/>+{r.co_owner_count} co-owner(s) share this score</div>
                        )}
                      </td>
                      <td className="text-xs text-[color:var(--text-secondary)]">{(r.fandom_names || []).join(", ") || "—"}</td>
                      <td className="text-right">
                        <span className={`inline-flex items-baseline gap-1 rounded-md border px-2 py-1 ${badgeCls(r.score)}`}>
                          <span className="font-heading text-lg">{r.score === null || r.score === undefined ? "—" : Math.round(r.score)}</span>
                          <span className="text-[10px] uppercase">/100</span>
                        </span>
                        <div className="text-[10px] text-[color:var(--text-secondary)] mt-0.5">{statusLabel(r.score)}</div>
                      </td>
                      <td className="text-right pr-3">
                        <button className="text-xs text-[color:var(--gold)] flex items-center gap-1"
                                onClick={() => setExpanded((s) => ({ ...s, [key]: !s[key] }))}
                                data-testid={`report-toggle-${key}`}>
                          {ex ? <ChevronUp size={12}/> : <ChevronDown size={12}/>} Details
                        </button>
                      </td>
                    </tr>
                    {ex && (
                      <tr className="border-t border-[color:var(--border)] bg-[color:var(--bg-surface-2)]/40">
                        <td colSpan={6} className="p-3">
                          <div className="text-xs text-[color:var(--text-secondary)] mb-2">
                            Store: <span className="text-[color:var(--text-primary)]">{r.store_name}</span> ·
                            Manager: <span className="text-[color:var(--text-primary)]">{r.manager_name}</span> ·
                            Cleanliness: <span className="capitalize">{(r.cleanliness_status || "—").replace(/_/g, " ")}</span>
                          </div>
                          {r.breakdown.length === 0 ? (
                            <div className="text-xs text-[color:var(--text-secondary)]">No standard breakdown captured.</div>
                          ) : (
                            <table className="w-full text-xs">
                              <thead className="text-left">
                                <tr className="label-caps"><th className="p-1">Standard</th><th className="p-1 text-right">Score</th><th className="p-1 text-right">Weight</th><th className="p-1 text-right">Contribution</th></tr>
                              </thead>
                              <tbody>
                                {r.breakdown.map((b) => (
                                  <tr key={b.standard_id} className="border-t border-[color:var(--border)]/50">
                                    <td className="p-1">{b.name_snapshot}</td>
                                    <td className="p-1 text-right">{b.score_percentage === null ? "—" : `${Math.round(b.score_percentage)}%`}</td>
                                    <td className="p-1 text-right">{Math.round(b.weight_snapshot)}%</td>
                                    <td className="p-1 text-right text-[color:var(--gold)]">{b.weighted_contribution === null ? "—" : b.weighted_contribution.toFixed(2)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
